/**
 * ConnectAccounts.jsx
 * Attaches a Facebook Page — and its linked Instagram account, if it has one —
 * to a brand.
 *
 * The Instagram question is answered by the API, not by appearances: a Page
 * with no `instagram` is one Meta will not let us publish to, however connected
 * it looks in Business Suite.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api.js';
import { Link, Warning, Check, Refresh } from './Icons.jsx';

export default function ConnectAccounts({ brand, accounts = [], onChanged }) {
  const [status, setStatus] = useState(null);
  const [pages, setPages] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const facebook = accounts.find((a) => a.platform === 'facebook' && a.status === 'connected');
  const instagram = accounts.find((a) => a.platform === 'instagram' && a.status === 'connected');

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await api.meta.status());
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  async function loadPages() {
    setBusy(true);
    setError(null);
    try {
      const { pages: list } = await api.meta.pages();
      setPages(list);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function attach(page) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.meta.connect(brand.id, page.id);
      setResult(res);
      setPages(null);
      onChanged?.();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function detach(account) {
    if (!confirm(`Disconnect ${account.display_name} from ${brand.name}?`)) return;
    setBusy(true);
    try {
      await api.meta.disconnect(account.id);
      onChanged?.();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  // ---------- Not signed in to Facebook at all ----------
  if (status && !status.connected) {
    return (
      <div className="row">
        <span className="tile tile--sm tile--warn">
          <Link size={17} />
        </span>
        <div>
          <div className="row__label">Facebook &amp; Instagram</div>
          <div className="row__sub">
            Sign in with the Facebook account that administers your Pages. One sign-in covers
            every brand.
          </div>
        </div>
        <div className="row__end">
          <a className="btn btn--primary btn--sm" href={api.meta.signInUrl}>
            Connect Facebook
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {status?.permissions && <Permissions report={status.permissions} />}

      {result?.note && (
        <div className="notice notice--info">
          <Warning />
          <span>{result.note}</span>
        </div>
      )}

      {/* ---------- Connected destinations ---------- */}
      {facebook && (
        <div className="row">
          <span className="tile tile--sm tile--ok">
            <Check size={17} />
          </span>
          <div>
            <div className="row__label">Facebook — {facebook.display_name}</div>
            <div className="row__sub">Page {facebook.fb_page_id}</div>
          </div>
          <div className="row__end">
            <button className="btn btn--sm" onClick={() => detach(facebook)} disabled={busy}>
              Disconnect
            </button>
          </div>
        </div>
      )}

      {instagram && (
        <div className="row">
          <span className="tile tile--sm tile--ok">
            <Check size={17} />
          </span>
          <div>
            <div className="row__label">Instagram — {instagram.display_name}</div>
            <div className="row__sub">Publishes through the linked Page</div>
          </div>
          <div className="row__end">
            <button className="btn btn--sm" onClick={() => detach(instagram)} disabled={busy}>
              Disconnect
            </button>
          </div>
        </div>
      )}

      {facebook && !instagram && (
        <div className="row">
          <span className="tile tile--sm tile--warn">
            <Warning size={17} />
          </span>
          <div>
            <div className="row__label">No Instagram for this brand</div>
            <div className="row__sub">
              Posts will go to Facebook only. Link an Instagram Business account to this Page in
              Meta Business Suite, then reconnect.
            </div>
          </div>
        </div>
      )}

      {/* ---------- Page picker ---------- */}
      {pages && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card__head">
            <div className="card__head-text">
              <h3>Choose the Page for {brand.name}</h3>
              <p>Instagram is attached automatically wherever it is genuinely linked.</p>
            </div>
          </div>

          {pages.length === 0 && (
            <p className="mono">
              This Facebook account administers no Pages. Check you signed in with the right
              account.
            </p>
          )}

          <div className="rows">
            {pages.map((page) => (
              <div className="row" key={page.id}>
                <span className={`tile tile--sm ${page.instagram ? 'tile--ok' : 'tile--warn'}`}>
                  <Link size={17} />
                </span>
                <div>
                  <div className="row__label">{page.name}</div>
                  <div className="row__sub">
                    {page.instagram
                      ? `Instagram @${page.instagram.username} linked`
                      : 'No linked Instagram — Facebook only'}
                  </div>
                </div>
                <div className="row__end">
                  <button
                    className="btn btn--primary btn--sm"
                    onClick={() => attach(page)}
                    disabled={busy}
                  >
                    Use for {brand.name}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------- Actions ---------- */}
      <div className="btn-row" style={{ marginTop: 12 }}>
        <button className="btn btn--sm" onClick={loadPages} disabled={busy}>
          <Refresh size={16} />
          {busy ? 'Working…' : facebook ? 'Change Page' : 'Choose a Page'}
        </button>

        {status?.connected && (
          <span className="badge">
            Facebook login valid
            {status.daysRemaining != null ? ` · ${status.daysRemaining} days left` : ''}
          </span>
        )}
        {/* Always available: adding a permission in the Meta dashboard does
            nothing until you sign in again, and that can happen at any time —
            not only when the token is close to expiring. */}
        {status?.connected && (
          <a className="btn btn--sm" href={api.meta.signInUrl}>
            <Refresh size={16} />
            {status.needsRefresh ? 'Renew sign-in' : 'Reconnect Facebook'}
          </a>
        )}
      </div>
    </>
  );
}


/**
 * What Meta has actually granted, feature by feature.
 *
 * Asked of Meta rather than read off the dashboard, because those two things
 * disagree constantly: adding a permission to the login configuration changes
 * nothing until the accounts are reconnected, and neither screen shows the
 * difference. Without this the only way to find out whether a permission had
 * taken effect was to use the feature and see whether it failed.
 *
 * The exact scope names are shown, not hidden behind friendly labels — they
 * are what has to be typed into the Meta dashboard, so they have to be
 * copyable.
 */
function Permissions({ report }) {
  const [open, setOpen] = useState(false);

  if (!report.known) {
    return (
      <div className="notice notice--info">
        <Warning />
        <span>{report.note}</span>
      </div>
    );
  }

  const missing = report.features.filter((f) => !f.ok);

  return (
    <>
      {missing.length === 0 ? (
        <div className="notice notice--info">
          <Check />
          <span>Every permission Kreeboom uses has been granted.</span>
        </div>
      ) : (
        <div className="notice notice--error">
          <Warning />
          <div>
            <span>
              {missing.length} of {report.features.length} features are missing a permission:{' '}
              {missing.map((f) => f.label).join(', ')}.
            </span>
            <p className="hint" style={{ marginTop: 4 }}>
              Add the permissions below to the <strong>Kreeboom publishing</strong> login
              configuration in the Meta dashboard, then press <strong>Reconnect Facebook</strong>.
              Adding them without reconnecting changes nothing.
            </p>
          </div>
        </div>
      )}

      <details className="details" open={missing.length > 0} onToggle={(e) => setOpen(e.target.open)}>
        <summary>
          What Meta says this login can do
          {open ? '' : ` (${report.features.length - missing.length} of ${report.features.length} working)`}
        </summary>

        <div className="rows" style={{ marginTop: 12 }}>
          {report.features.map((feature) => (
            <div className="row" key={feature.key}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="row__label">{feature.label}</div>
                <div className="row__sub">
                  {feature.ok ? (
                    feature.scopes.join(', ')
                  ) : (
                    <>
                      Missing <b className="mono-wrap">{feature.missing.join(', ')}</b> — {feature.without}
                    </>
                  )}
                </div>
              </div>
              <div className="row__end">
                <span className={`badge${feature.ok ? ' badge--ok' : ' badge--warn'}`}>
                  {feature.ok ? 'working' : 'not granted'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {missing.length > 0 && (
          <p className="hint" style={{ marginTop: 12 }}>
            Everything still missing, ready to copy:{' '}
            <b className="mono-wrap">
              {[...new Set(missing.flatMap((f) => f.missing))].join(' ')}
            </b>
          </p>
        )}
      </details>
    </>
  );
}
